// src/api/index.js
//
// Same-origin proxy: SWA Function in production, CRA proxy (src/setupProxy.js)
// in local development.
import axios from 'axios';
import { createAppTransport } from '@1823-partners/core';

const api = createAppTransport({ axios, appName: 'otti' });

export const fetchOttiData = async (asofdt) => {
  return api.get(`/otti-data?asofdt=${asofdt}`);
};

export const fetchOttiDates = async () => {
  return api.get('/otti-dates');
};

export const fetchIntexAssumptions = async () => {
  return api.get('/intex-assumptions');
};

export const runOtti = async (user, asofdt, runId) => {
  return api.post('/run-otti', { user, asofdt, run_id: runId }, {
    timeout: 240000 // 4 minutes timeout to accommodate 3+ minute processing time
  });
};

// Lightweight completion signal for a long Run OTTI. The run can outlast the
// SWA gateway (the proxy 500s while Beacon keeps working), so handleRunOtti
// captures latest_modified before firing, then polls this until it advances.
export const getRunStatus = async (asofdt) => {
  return api.get('/run-status', {
    params: { asofdt },
    timeout: 30000 // 30 seconds timeout
  });
};

export const saveOtti = async (ottiData) => {
  return api.post('/save-otti', ottiData, {
    timeout: 120000 // 2 minutes timeout for save operation
  });
};

export const fetchPreviousLotIds = async (asofdt) => {
  return api.get('/prev-lot-ids', {
    params: { asofdt },
    timeout: 30000 // 30 seconds timeout
  });
};

export const getCurrentSpreadUploadStatus = async (asofdt) => {
  return api.get('/current-spread-upload-status', {
    params: { asofdt },
    timeout: 30000 // 30 seconds timeout
  });
};

export const getRunHistory = async (asofdt) => {
  return api.get('/run-history', {
    params: { asofdt },
    timeout: 30000 // 30 seconds timeout
  });
};

// Multi-sheet upload destinations. The OTTI workbook's "Current Spread" and
// "Ratings Downgrades" tabs feed dedicated append-only tables (not the grid),
// so they POST directly rather than going through the grid Save flow.
export const uploadCurrentSpreads = async (rows) => {
  return api.post('/upload-current-spreads', rows, {
    timeout: 120000 // 2 minutes timeout for upload operation
  });
};

export const uploadRatingsDowngrades = async (rows) => {
  return api.post('/upload-ratings-downgrades', rows, {
    timeout: 120000 // 2 minutes timeout for upload operation
  });
};

// Comment-template rules editor. The save is gated by otti.impairments:write
// server-side. Priority is derived from row order, so `templates` is the
// ordered list of rules. `loaded` is the [{PRIORITY, MODIFIED_DATE}] snapshot
// the editor loaded, sent back unchanged: a save by someone else since then is
// a 409 instead of being overwritten.
export const getCommentTemplates = async () => {
  return api.get('/comment-templates');
};

export const saveCommentTemplates = async (templates, loaded) => {
  return api.post('/save-comment-templates', { templates, loaded }, {
    timeout: 60000 // 1 minute timeout for save operation
  });
};

// Distinct SEC_OWNER / STYPEL3 values from the OTTI universe, used to populate
// the comment-template editor's Sec Owner (dropdown) and Security Type (typeable
// combobox) cells. Open read.
export const getOttiFieldValues = async () => {
  return api.get('/otti-field-values');
};

// Export the api client for direct access if needed
export default api;
